/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import * as Yup from "yup";

import {
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  StackItem,
} from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import CheckboxController from "Components/ReactHookForm/CheckboxController/CheckboxController";
import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import TextFieldController from "Components/ReactHookForm/TextFieldController/TextFieldController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { SidePanelConstants } from "Constants/ApplicationConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  updateApplicationStatusesAndRemoveFlagsReset,
  updateApplicationTechnologyAreaAndResetScores,
} from "Dataflows/ApplicationDetails/ApplicationDetailsAction";
import {
  getUpdateTechnologyAreaOperationStatus,
  updateTechnologyAreaErrorMessage,
} from "Dataflows/ApplicationDetails/ApplicationDetailsSelector";
import { getTechnologyAreas } from "Dataflows/TechnologyArea/TechnologyAreaAction";
import { getAllTechnologyAreaOptionList } from "Dataflows/TechnologyArea/TechnologyAreaSelector";
import { IApplicationFormTechnologyFocusAreaUpdateData } from "Interfaces/Application/IApplicationFormTechnologyFocusAreaUpdateData";

import { IUpdateTechnologyAreaSitePanelProps } from "./IUpdateTechnologyAreaSitePanel";

export const UpdateTechnologyAreaSitePanel = (
  props: IUpdateTechnologyAreaSitePanelProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, applicationFormId, applicationTechnologyFocusAreas } = props;
  const dispatch = useDispatch();
  const updateTechnologyAreaOpStatus = useSelector(getUpdateTechnologyAreaOperationStatus);
  const updateTechnologyAreaError = useSelector(updateTechnologyAreaErrorMessage);
  const technologyAreaOptions = useSelector(getAllTechnologyAreaOptionList);
  const [isLoadingTechnologyAreas, setIsLoadingTechnologyAreas] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      justification: "",
      applicationTechnologyFocusAreas: applicationTechnologyFocusAreas,
      isResetScores: [false, false],
    },
    resolver: yupResolver(
      Yup.object().shape({
        justification: Yup.string().required("Justification is required").max(3900),
        applicationTechnologyFocusAreas: Yup.array().test(
          "SecondTA",
          "Second Technology Area is the same as the first Technology Area.",
          function (value: any) {
            if (
              value.length === 2 &&
              value[0].technologyFocusAreaName === value[1].technologyFocusAreaName
            ) {
              return this.createError({
                path: "applicationTechnologyFocusAreas[1].technologyFocusAreaName",
                message: "Second Technology Area is the same as the first Technology Area.",
              });
            }
            return true;
          }
        ),
      })
    ),
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const save = () => {
    return handleSubmit((formData) => {
      // Format data for API endpoint request
      const tfaUpdateData = (): IApplicationFormTechnologyFocusAreaUpdateData[] => {
        return formData.applicationTechnologyFocusAreas.map((item, i) => {
          const temp = {
            currentTechnologyFocusAreaName: "",
            newTechnologyFocusAreaName: item.technologyFocusAreaName,
            isResetScore: formData.isResetScores[i],
          };
          temp.currentTechnologyFocusAreaName = applicationTechnologyFocusAreas.filter(
            (x) => x.technologyFocusAreaId === item.technologyFocusAreaId
          )[0].technologyFocusAreaName;
          return temp;
        });
      };

      dispatch(
        updateApplicationTechnologyAreaAndResetScores(
          applicationFormId,
          tfaUpdateData(),
          formData.justification
        )
      );
    });
  };

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    dispatch(updateApplicationStatusesAndRemoveFlagsReset());
    reset();
  };

  useEffect(() => {
    if (updateTechnologyAreaOpStatus === OperationStatus.Succeeded) {
      dismissPanel();
    }
  }, [updateTechnologyAreaOpStatus]);

  useEffect(() => {
    // If we don't have technology areas in state, fetch them
    if (technologyAreaOptions.length === 0 && !isLoadingTechnologyAreas && isPanelOpen) {
      dispatch(getTechnologyAreas(TenantTypesEnum.MVP));
      setIsLoadingTechnologyAreas(true);
    }
  }, [technologyAreaOptions, isLoadingTechnologyAreas, isPanelOpen]);

  const { fields } = useFieldArray({ control, name: "applicationTechnologyFocusAreas" });

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isPanelOpen}
        title="Update the Technology Area"
        saveButtonLabel="Save"
        error={updateTechnologyAreaError}
        disableActions={updateTechnologyAreaOpStatus === OperationStatus.Pending}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <MessageBar messageBarType={MessageBarType.info}>
            {SidePanelConstants.UpdateTechnologyAreaSidePanelInformation}
          </MessageBar>
          {technologyAreaOptions.length === 0 ? (
            <Spinner size={SpinnerSize.large} />
          ) : (
            <>
              {fields.map((field, index) => {
                return (
                  <Stack.Item key={field.id}>
                    <StackItem styles={{ root: { paddingBottom: "1.3rem" } }}>
                      <ComboBoxController
                        control={control}
                        errors={errors}
                        inputName={`applicationTechnologyFocusAreas[${index}].technologyFocusAreaName`}
                        isRequired={false}
                        labelDisplayName={field.technologyFocusAreaName}
                        options={technologyAreaOptions}
                        isDisabled={updateTechnologyAreaOpStatus === OperationStatus.Pending}
                        defaultSelectedKey={
                          technologyAreaOptions.find(
                            (opt) => opt.text === field.technologyFocusAreaName
                          )?.text
                        }
                      />
                    </StackItem>

                    <StackItem>
                      <CheckboxController
                        control={control}
                        errors={errors}
                        labelDisplayName="Reset Score"
                        inputName={`isResetScores[${index}]`}
                      />
                    </StackItem>
                  </Stack.Item>
                );
              })}
              <StackItem>
                <TextFieldController
                  inputName="justification"
                  control={control}
                  errors={errors}
                  isRequired
                  isMultiline
                  hasTooltip
                  labelDisplayName="Justification"
                  toolTipDisplayText="Please explain why this application needs additional review"
                  characterCountLimit={3900}
                />
              </StackItem>
            </>
          )}
        </Stack>
      </SidePanel>
    </form>
  );
};
